import { Undo2 } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import type {
  Classification,
  DecisionCoKind,
  DecisionPatchBody,
  DecisionReason,
  TriageDecision,
} from "@/hooks/use-triage";
import { useClearDecision, usePatchDecision } from "@/hooks/use-triage";
import { cn } from "@/lib/utils";

const REVIEW_REQUIRES_QUESTION_MSG =
  "A triage item requires at least one question before it can be placed in Review status.";

type Step = "status" | "reason" | "classification" | "coKind" | null;
type StatusOption = "in-scope" | "in-review" | "out-of-scope";

interface TileOption<T extends string> {
  id: T;
  label: string;
  /** Optional small sub-label rendered under the main label (e.g. "L1", "Change order"). */
  sub?: string;
  /** Longer description shown as a tooltip on hover (the `title` attribute). */
  help: string;
  tone?: "scope" | "review" | "out";
}

const STATUS_OPTIONS: ReadonlyArray<TileOption<StatusOption>> = [
  {
    id: "in-scope",
    label: "In Scope",
    tone: "scope",
    help: "Commit to this in the current project. Adds to the baseline scope.",
  },
  {
    id: "in-review",
    label: "Review",
    tone: "review",
    help: "Need more input from the team before deciding. Stays flagged.",
  },
  {
    id: "out-of-scope",
    label: "Out of Scope",
    tone: "out",
    help: "Will not commit to this in the current project. Pick a reason next.",
  },
];

const REASON_OPTIONS: ReadonlyArray<TileOption<DecisionReason>> = [
  {
    id: "change-order",
    label: "Want it now",
    sub: "Change order",
    help: "Client wants it now — spawn a change order with delta cost & timeline.",
  },
  {
    id: "roadmap",
    label: "Want it later",
    sub: "Roadmap",
    help: "Park for a future phase. Tracked but not in this commitment.",
  },
  {
    id: "reject",
    label: "Don't want it",
    sub: "Not a fit",
    help: "Decline outright — doesn't fit project goals or constraints.",
  },
];

const COKIND_OPTIONS: ReadonlyArray<TileOption<DecisionCoKind>> = [
  {
    id: "swap",
    label: "Swap with something",
    sub: "Replace existing scope",
    help: "Trade this in for an item already in scope at the same level. Net-zero on cost & timeline.",
  },
  {
    id: "new",
    label: "New scope",
    sub: "Net-new addition",
    help: "Add this on top of existing scope. Spawns a change order with delta cost & timeline.",
  },
];

const LEVEL_OPTIONS: ReadonlyArray<TileOption<Classification>> = [
  {
    id: "L1",
    label: "Deliverable",
    sub: "L1",
    help: "Top-level outcome. A whole capability area.",
  },
  { id: "L2", label: "Epic", sub: "L2", help: "A major workstream inside an L1." },
  {
    id: "L3",
    label: "User story",
    sub: "L3",
    help: "A discrete user-facing story under an L2.",
  },
  {
    id: "AC",
    label: "Acceptance criterion",
    sub: "AC",
    help: "A specific acceptance criterion on an existing story.",
  },
];

const STATUS_LABEL: Record<string, string> = {
  "in-scope": "In Scope",
  "in-review": "Review",
  "out-of-scope": "Out of Scope",
};
const REASON_LABEL: Record<string, string> = {
  "change-order": "Want it now",
  roadmap: "Want it later",
  reject: "Don't want it",
};
const COKIND_LABEL: Record<string, string> = {
  swap: "Swap with",
  new: "New scope",
};
const LEVEL_LABEL: Record<string, string> = {
  L1: "Deliverable",
  L2: "Epic",
  L3: "User story",
  AC: "Acceptance criterion",
};

function activeStep(d: TriageDecision | null): Step {
  if (!d) return "status";
  if (d.status === "in-review") return null;
  if (d.status === "in-scope") return d.classification ? null : "classification";
  if (d.status === "out-of-scope") {
    if (!d.reason) return "reason";
    if (d.reason !== "change-order") return null;
    if (!d.classification) return "classification";
    if (!d.coKind) return "coKind";
    return null;
  }
  return "status";
}

interface DecisionWizardProps {
  itemId: string;
  decision: TriageDecision | null;
  suggestedStatus: string;
  linkedQuestionCount: number;
}

export function DecisionWizard({
  itemId,
  decision,
  suggestedStatus,
  linkedQuestionCount,
}: DecisionWizardProps) {
  const patch = usePatchDecision(itemId);
  const clear = useClearDecision(itemId);
  const step = activeStep(decision);

  function send(body: DecisionPatchBody) {
    patch.mutate(body);
  }

  function pickStatus(id: StatusOption) {
    if (id === "in-review" && linkedQuestionCount === 0) {
      toast.error(REVIEW_REQUIRES_QUESTION_MSG);
      return;
    }
    send({ status: id, reason: null, classification: null, coKind: null });
  }

  // Keyboard 1-4 for active step option (when not in input/textarea)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!step) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const n = Number(e.key);
      if (!Number.isInteger(n) || n < 1) return;

      if (step === "status" && n <= STATUS_OPTIONS.length) {
        e.preventDefault();
        const opt = STATUS_OPTIONS[n - 1];
        pickStatus(opt.id);
      } else if (step === "reason" && n <= REASON_OPTIONS.length) {
        e.preventDefault();
        send({ reason: REASON_OPTIONS[n - 1].id, classification: null, coKind: null });
      } else if (step === "classification" && n <= LEVEL_OPTIONS.length) {
        e.preventDefault();
        send({ classification: LEVEL_OPTIONS[n - 1].id });
      } else if (step === "coKind" && n <= COKIND_OPTIONS.length) {
        e.preventDefault();
        send({ coKind: COKIND_OPTIONS[n - 1].id });
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, linkedQuestionCount]);

  const trail: Array<{ key: string; label: string; tone?: "scope" | "review" | "out" }> = [];
  if (decision?.status) {
    const tone =
      decision.status === "in-scope"
        ? "scope"
        : decision.status === "in-review"
          ? "review"
          : decision.status === "out-of-scope"
            ? "out"
            : undefined;
    trail.push({
      key: "status",
      label: STATUS_LABEL[decision.status] ?? decision.status,
      tone,
    });
  }
  if (decision?.reason) {
    trail.push({ key: "reason", label: REASON_LABEL[decision.reason] ?? decision.reason });
  }
  if (decision?.classification) {
    trail.push({
      key: "class",
      label: LEVEL_LABEL[decision.classification] ?? decision.classification,
    });
  }
  if (decision?.coKind) {
    trail.push({ key: "co", label: COKIND_LABEL[decision.coKind] ?? decision.coKind });
  }

  const stepLabel =
    step === "status"
      ? "Decide"
      : step === "reason"
        ? "Out of Scope decision"
        : step === "classification"
          ? decision?.status === "in-scope"
            ? "Classify in the framework"
            : "Classify the change"
          : step === "coKind"
            ? "Change order — swap or add?"
            : null;

  return (
    <section className="space-y-3">
      {trail.length > 0 && (
        <div className="flex items-center flex-wrap gap-2">
          {trail.map((t, i) => (
            <span key={t.key} className="inline-flex items-center gap-2">
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold border",
                  trailToneClass(t.tone),
                )}
              >
                {t.label}
              </span>
              {i < trail.length - 1 && <span className="text-muted-foreground text-sm">›</span>}
            </span>
          ))}
          <button
            type="button"
            onClick={() => clear.mutate()}
            disabled={clear.isPending}
            className="ml-1 inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] px-2.5 py-0.5 text-[11px] text-muted-foreground hover:text-foreground hover:border-foreground/50"
            title="Clear this decision"
          >
            <Undo2 className="h-3 w-3" />
            Undo
          </button>
        </div>
      )}

      {!decision && (
        <p className="text-xs text-muted-foreground">
          AI suggested{" "}
          <span className="font-medium text-foreground">
            {STATUS_LABEL[suggestedStatus] ?? suggestedStatus}
          </span>
          . Confirm or override below.
        </p>
      )}

      {stepLabel && (
        <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold pt-1">
          {stepLabel}
        </div>
      )}

      {step === "status" && (
        <TileGrid
          options={STATUS_OPTIONS}
          variant="status"
          onPick={(id) => pickStatus(id as StatusOption)}
        />
      )}

      {step === "reason" && (
        <TileGrid
          options={REASON_OPTIONS}
          variant="neutral"
          onPick={(id) =>
            send({ reason: id as DecisionReason, classification: null, coKind: null })
          }
        />
      )}

      {step === "classification" && (
        <TileGrid
          options={LEVEL_OPTIONS}
          variant="neutral"
          onPick={(id) => send({ classification: id as Classification })}
        />
      )}

      {step === "coKind" && (
        <TileGrid
          options={COKIND_OPTIONS}
          variant="neutral"
          onPick={(id) => send({ coKind: id as DecisionCoKind })}
        />
      )}

      {!step && decision?.status === "in-review" && (
        <div className="rounded-md border-l-2 border-warning bg-warning-bg/40 px-4 py-3 text-sm text-foreground/85">
          Flagged for follow-up. A decision is still needed.
        </div>
      )}
    </section>
  );
}

function trailToneClass(tone?: "scope" | "review" | "out"): string {
  if (tone === "scope")
    return "bg-[#EAF6EE] text-[#1F6B43] border-[#C6E5D2] dark:bg-[#0d2418] dark:text-[#7ed4a3] dark:border-[#1d4a30]";
  if (tone === "review")
    return "bg-[#FFF5DC] text-[#7A5210] border-[#F0DC9D] dark:bg-[#261c08] dark:text-[#f0c879] dark:border-[#544020]";
  if (tone === "out")
    return "bg-[#FBEAEA] text-[#8B2727] border-[#ECC8C8] dark:bg-[#2a1414] dark:text-[#f08e8e] dark:border-[#5a2828]";
  return "bg-nymbl-sky text-nymbl-navy border-nymbl-sky";
}

function TileGrid<T extends string>({
  options,
  variant,
  onPick,
}: {
  options: ReadonlyArray<TileOption<T>>;
  variant: "status" | "neutral";
  onPick: (id: T) => void;
}) {
  const cols =
    options.length === 4 ? "grid-cols-4" : options.length === 2 ? "grid-cols-2" : "grid-cols-3";

  return (
    <div className={cn("grid gap-2", cols)}>
      {options.map((opt, i) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onPick(opt.id)}
          title={opt.help}
          className={cn(
            "relative flex flex-col items-center justify-center rounded-xl border-2 text-center transition-all min-h-[78px] px-3 py-3.5",
            "hover:-translate-y-0.5 hover:shadow-md",
            variant === "status" ? statusTileClass(opt.tone) : neutralTileClass(),
          )}
        >
          <span className="font-display font-semibold text-[14px] leading-tight">{opt.label}</span>
          {opt.sub && <span className="text-[11px] mt-1 leading-snug opacity-80">{opt.sub}</span>}
          <span className="absolute top-1.5 right-2 font-mono text-[10px] opacity-60" aria-hidden>
            {i + 1}
          </span>
        </button>
      ))}
    </div>
  );
}

function statusTileClass(tone?: "scope" | "review" | "out"): string {
  if (tone === "scope")
    return "bg-[#EAF6EE] border-[#C6E5D2] text-[#1F6B43] hover:bg-[#DDEFE2] hover:border-[#2EA86A] dark:bg-[#0d2418] dark:border-[#1d4a30] dark:text-[#7ed4a3] dark:hover:bg-[#13351f] dark:hover:border-[#2EA86A]";
  if (tone === "review")
    return "bg-[#FFF5DC] border-[#F0DC9D] text-[#7A5210] hover:bg-[#FCEBC1] hover:border-[#D9A82F] dark:bg-[#261c08] dark:border-[#544020] dark:text-[#f0c879] dark:hover:bg-[#33270c] dark:hover:border-[#D9A82F]";
  if (tone === "out")
    return "bg-[#FBEAEA] border-[#ECC8C8] text-[#8B2727] hover:bg-[#F5D8D8] hover:border-[#C45A5A] dark:bg-[#2a1414] dark:border-[#5a2828] dark:text-[#f08e8e] dark:hover:bg-[#371919] dark:hover:border-[#C45A5A]";
  return neutralTileClass();
}

function neutralTileClass(): string {
  return "bg-card border-[var(--color-border)] text-foreground hover:border-nymbl-blue hover:bg-nymbl-sky/30";
}
